import { useState } from 'react';
import styled from 'styled-components';

import { Text } from '../../cdl/Text/Text';
import { CloselinkMap, Position } from '../Map/CloselinkMap';
import { MarkerIcon, PositionMarker } from '../PositionMarker/PositionMarker';
import { Box } from '../ui/Box';
import { theme } from '../ui/theme';

const MapWrapper = styled(Box)`
    width: 100%;
    height: 200px;
    padding: 2px;
`;

const List = styled.ul`
    list-style-type: none;
    overflow: auto;
    height: calc(100% - 180px);
    padding-left: 0;
`;

const PositionItem = styled.li`
    display: flex;
    flex-direction: column;
    padding-left: 40px;
    position: relative;

    &:hover {
        cursor: pointer;
        background-color: ${theme.colors.background.subtle};
    }
`;

const IconWrapper = styled(Box)`
    position: absolute;
    left: 11px;
    top: 11px;
    height: 16px;
    width: 16px;
    z-index: ${theme.zIndex[200]};
`;

interface PositionDisplayProps {
    positionTitle: string;
    primaryDescription: string;
    secondaryDescription: string;
    latitude: number;
    longitude: number;
}

export const PositionDisplay = ({
    positionTitle,
    primaryDescription,
    secondaryDescription,
    latitude,
    longitude,
}: PositionDisplayProps) => {
    const [mapCamera, setMapCamera] = useState<{ center: Position; zoom: number } | undefined>(undefined);

    const position: Position = {
        lat: latitude,
        lng: longitude,
    };

    const markerColor = theme.colors.accent.emphasis;

    const handlePositionClick = () => {
        setMapCamera({
            center: position,
            zoom: 7,
        });
    };

    const handleCameraChanged = (center: Position, zoom: number) => {
        setMapCamera({ center, zoom });
    };

    return (
        <div>
            <MapWrapper>
                <CloselinkMap
                    places={[position]}
                    center={mapCamera?.center}
                    zoom={mapCamera?.zoom}
                    fitToBounds={!mapCamera}
                    onCameraChanged={handleCameraChanged}
                >
                    {[<PositionMarker key="position" lat={position.lat} lng={position.lng} color={markerColor} />]}
                </CloselinkMap>
            </MapWrapper>
            <List>
                <PositionItem onClick={handlePositionClick}>
                    <Box paddingBottom={8} paddingTop={8}>
                        <IconWrapper>
                            <MarkerIcon color={markerColor} />
                        </IconWrapper>

                        <Text variant="small" fontWeight={600} color="foreground.default">
                            {positionTitle}
                        </Text>

                        <Box display="flex" flexDirection="column" color="foreground.default">
                            {primaryDescription}
                            <Text variant="small" color="foreground.subtle">
                                {secondaryDescription}
                            </Text>
                        </Box>
                    </Box>
                </PositionItem>
            </List>
        </div>
    );
};
